/**
 * Munja Trans GmbH · Transport-Plattform — React entrypoint
 *
 * ==========================================================================
 *   © 2026 Aleksandar Ichkov. Alle Rechte vorbehalten / All Rights Reserved.
 *   This application is designed, built and owned by Aleksandar Ichkov.
 *   Operated exclusively for Munja Trans GmbH and its sister companies.
 *   See /app/OWNERSHIP.md for full notice.
 * ==========================================================================
 */
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { TenantProvider } from "@/contexts/TenantContext";
import { PermissionsProvider } from "@/contexts/PermissionsContext";
import { FirmProvider } from "@/contexts/FirmContext";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import RequirePerm from "@/components/RequirePerm";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Mitarbeiter from "@/pages/Mitarbeiter";
import Abwesenheiten from "@/pages/Abwesenheiten";
import HrAbsenceHub from "@/pages/HrAbsenceHub";
import MitarbeiterProfile from "@/pages/MitarbeiterProfile";
import Fuhrpark from "@/pages/Fuhrpark";
import FuhrparkProfile from "@/pages/FuhrparkProfile";
import Zeiterfassung from "@/pages/Zeiterfassung";
import Lenkprotokoll from "@/pages/Lenkprotokoll";
import Dienstplan from "@/pages/Dienstplan";
import Dokumente from "@/pages/Dokumente";
import Audit from "@/pages/Audit";
import Reports from "@/pages/Reports";
import Einstellungen from "@/pages/Einstellungen";import Finanzen from "@/pages/Finanzen";
import ExportPage from "@/pages/ExportPage";
import ExportDownloads from "@/pages/ExportDownloads";
import OperationsNav from "@/pages/OperationsNav";
import Heute from "@/pages/Heute";
import Aufgaben from "@/pages/Aufgaben";
import Uebergaben from "@/pages/Uebergaben";
import Lenkerauskunft from "@/pages/Lenkerauskunft";
import AutoGenerator from "@/pages/AutoGenerator";
import AutoGeneratorBatch from "@/pages/AutoGeneratorBatch";
import AutoGenOverwrites from "@/pages/AutoGenOverwrites";
import Buchhaltung from "@/pages/Buchhaltung";
import SuperAdminTenants from "@/pages/SuperAdminTenants";
import SuperAdminPackages from "@/pages/SuperAdminPackages";
import SuperAdminHealth from "@/pages/SuperAdminHealth";
import SuperAdminExtensions from "@/pages/SuperAdminExtensions";
import SuperAdminBusinessOverview from "@/pages/SuperAdminBusinessOverview";
import Erweiterungen from "@/pages/Erweiterungen";
import Firmenprofil from "@/pages/Firmenprofil";
import Karten from "@/pages/Karten";
import Profilanfragen from "@/pages/Profilanfragen";
import ZeitAnfragen from "@/pages/ZeitAnfragen";
import SecurityDashboard from "@/pages/SecurityDashboard";
import AccountantPortal from "@/pages/AccountantPortal";
import PayrollSettings from "@/pages/PayrollSettings";
import ForcePasswordChangeGate from "@/components/ForcePasswordChangeGate";
import ImpersonationBar from "@/components/ImpersonationBar";

// Phase 5Z.9 — Fahrer-Portal
import "@/fahrer/i18n";
import { FahrerAuthProvider } from "@/fahrer/lib/auth";
import FahrerLayout from "@/fahrer/FahrerLayout";
import FahrerLogin from "@/fahrer/FahrerLogin";
import FahrerChangePassword from "@/fahrer/FahrerChangePassword";
import FahrerDashboard from "@/fahrer/FahrerDashboard";
import FahrerProfile from "@/fahrer/FahrerProfile";
// Lohnzettel temporarily disabled per user request — re-enable when ready
// import FahrerLohnzettel from "@/fahrer/FahrerLohnzettel";
import FahrerHandover from "@/fahrer/FahrerHandover";
import FahrerZeiterfassung from "@/fahrer/FahrerZeiterfassung";
import FahrerCalendar from "@/fahrer/FahrerCalendar";
import FahrerAufgaben from "@/fahrer/FahrerAufgaben";
import FahrerHr from "@/fahrer/FahrerHr";

import { ThemeProvider } from "@/contexts/ThemeContext";
import { FOCUS_MODE } from "@/lib/featureFlags";
import "@/App.css";

function Shell() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/t/:slug/login" element={<Login />} />
        <Route path="/steuerberater-portal" element={<AccountantPortal />} />
        {/* Phase 5Z.9 — Fahrer-Portal (separate auth realm, mobile-first) */}
        <Route path="/fahrer/login" element={
          <FahrerAuthProvider><FahrerLogin /></FahrerAuthProvider>
        } />
        <Route path="/fahrer/change-password" element={
          <FahrerAuthProvider><FahrerChangePassword /></FahrerAuthProvider>
        } />
        <Route path="/fahrer" element={
          <FahrerAuthProvider><FahrerLayout /></FahrerAuthProvider>
        }>
          <Route index element={<FahrerDashboard />} />
          <Route path="profile" element={<FahrerProfile />} />
          <Route path="vehicle" element={<FahrerHandover />} />
          <Route path="zeit" element={<FahrerZeiterfassung />} />
          <Route path="calendar" element={<FahrerCalendar />} />
          <Route path="aufgaben" element={<FahrerAufgaben />} />
          <Route path="hr" element={<FahrerHr />} />
          {/* Lohnzettel temporarily disabled per Phase 5Z.13 user request — re-enable when ready */}
        </Route>
        <Route element={<Layout />}>
          <Route index element={<Dashboard />} />
          <Route path="operations" element={<OperationsNav />}>
            <Route index element={<Navigate to="heute" replace />} />
            <Route path="heute" element={<Heute />} />
            <Route path="zeiterfassung" element={<Zeiterfassung />} />
            <Route path="auto-generator" element={<AutoGenerator />} />
            <Route path="auto-generator-batch" element={<AutoGeneratorBatch />} />
            <Route path="auto-generator-overwrites" element={<AutoGenOverwrites />} />
            <Route path="lenkprotokoll" element={<Lenkprotokoll />} />
            <Route path="dienstplan" element={<Dienstplan />} />
            <Route path="aufgaben" element={<Aufgaben />} />
            <Route path="lenkerauskunft" element={<Lenkerauskunft />} />
            <Route path="buchhaltung" element={<Buchhaltung />} />
          </Route>
          <Route path="mitarbeiter" element={<Mitarbeiter />} />
          <Route path="mitarbeiter/:id" element={<MitarbeiterProfile />} />
          <Route path="abwesenheiten" element={<RequirePerm module="abwesenheiten"><Abwesenheiten /></RequirePerm>} />
          <Route path="hr" element={<RequirePerm module="abwesenheiten"><HrAbsenceHub /></RequirePerm>} />
          <Route path="hr/:tab" element={<RequirePerm module="abwesenheiten"><HrAbsenceHub /></RequirePerm>} />
          <Route path="profilanfragen" element={<Profilanfragen />} />
          <Route path="zeit-anfragen" element={<ZeitAnfragen />} />
          <Route path="fuhrpark" element={<Fuhrpark />} />
          <Route path="fuhrpark/uebergaben" element={<Uebergaben />} />
          <Route path="karten" element={<Karten />} />
          <Route path="fuhrpark/:id" element={<FuhrparkProfile />} />
          <Route path="dokumente" element={<Dokumente />} />
          <Route path="finanzen" element={<Finanzen />} />
          <Route path="export" element={<ExportPage />} />
          <Route path="exports/downloads" element={<ExportDownloads />} />
          <Route path="reports" element={<Reports />} />
          <Route path="audit" element={<Audit />} />
          <Route path="security" element={<SecurityDashboard />} />
          <Route path="einstellungen" element={<Einstellungen />} />
          <Route path="einstellungen/lohn-zeit" element={<PayrollSettings />} />
          <Route path="super-admin/tenants" element={FOCUS_MODE ? <Navigate to="/" replace /> : <SuperAdminTenants />} />
          <Route path="super-admin/packages" element={FOCUS_MODE ? <Navigate to="/" replace /> : <SuperAdminPackages />} />
          <Route path="super-admin/health" element={FOCUS_MODE ? <Navigate to="/" replace /> : <SuperAdminHealth />} />
          <Route path="super-admin/extensions" element={FOCUS_MODE ? <Navigate to="/" replace /> : <SuperAdminExtensions />} />
          <Route path="super-admin/cockpit" element={FOCUS_MODE ? <Navigate to="/" replace /> : <SuperAdminBusinessOverview />} />
          <Route path="erweiterungen" element={FOCUS_MODE ? <Navigate to="/" replace /> : <Erweiterungen />} />
          <Route path="firmenprofil" element={<Firmenprofil />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <TenantProvider>
        <AuthProvider>
          <PermissionsProvider>
            <FirmProvider>
              <div className="App">
                <ImpersonationBar />
                <Shell />
                <ForcePasswordChangeGate />
                <Toaster position="top-right" theme="dark" />
              </div>
            </FirmProvider>
          </PermissionsProvider>
        </AuthProvider>
      </TenantProvider>
    </ThemeProvider>
  );
}
